import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ExtraInfo, Order } from '../models/order';
import { map } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';
import { Account, UpdateAccount } from '../models/account';
import { SmsMessage } from '../models/sms';
import { Vehicle } from '../models/vehicle';
import { VehicleFile } from '../models/vehicle-file';
import { CourierFile } from '../models/courier-file';
import { Courier } from '../models/courier';
import { PhotoType } from '../modals/order-preview/components/photos/models/photo';
import { LocationPoint } from '../models/location-point';
import { PaymentQr } from '../models/payment-qr';

@Injectable({ providedIn: 'root' })
export class CourierRepo {

    constructor(
        private http: HttpClient
    ) {
    }

    sendEmail(orderId: string, emailAddress: string): Observable<any> {
        return this.http.post(`${environment.apiBaseUrl}/courier/email-ticket`, { orderId, emailAddress });
    }

    getOrders(): Observable<Order[]> {
        return this.http.get<Order[]>(`${environment.apiBaseUrl}/courier/get-orders`)
            .pipe(map((order) => plainToClass(Order, order)));
    }

    setOrderAsPaid(orderId: string): Observable<any> {
        return this.http.post(`${environment.apiBaseUrl}/courier/set-order-as-paid`, { orderId });
    }

    getPaymentQrByOrderId(orderId: string): Observable<PaymentQr> {
        return this.http.get<PaymentQr>(`${environment.apiBaseUrl}/courier/get-order-payment-qr/${orderId}`);
    }

    startDelivery(orderId: string): Observable<any> {
        return this.http.post(`${environment.apiBaseUrl}/courier/start-delivery`, { orderId });
    }

    endDelivery(orderId: string): Observable<any> {
        return this.http.post(`${environment.apiBaseUrl}/courier/end-delivery`, { orderId });
    }

    activateCourier(code: string): Observable<any> {
        return this.http.post(`${environment.apiBaseUrl}/courier/activate`, { code });
    }

    deActivateCourier(): Observable<any> {
        return this.http.post(`${environment.apiBaseUrl}/courier/deactivate`, {});
    }

    getAccount(): Observable<Account> {
        return this.http.get<Account>(`${environment.apiBaseUrl}/courier/get-account`);
    }

    sendSmsMessage(smsMessage: SmsMessage): Observable<unknown> {
        return this.http.post(`${environment.apiBaseUrl}/courier/send-sms`, smsMessage);
    }

    updateAccount(updateAccount: UpdateAccount): Observable<unknown> {
        return this.http.post(`${environment.apiBaseUrl}/courier/update-account`, updateAccount);
    }

    updateExtraInfo(extraInfo: ExtraInfo): Observable<unknown> {
        return this.http.post(`${environment.apiBaseUrl}/courier/update-extra-info`, extraInfo);
    }

    getVehicles(): Observable<Vehicle[]> {
        return this.http.get<Vehicle[]>(`${environment.apiBaseUrl}/courier/get-vehicles`);
    }

    getVehicleFiles(): Observable<VehicleFile[]> {
        return this.http.get<VehicleFile[]>(`${environment.apiBaseUrl}/courier/get-vehicle-files`);
    }

    getCourierFiles(): Observable<CourierFile[]> {
        return this.http.get<CourierFile[]>(`${environment.apiBaseUrl}/courier/get-courier-files`);
    }

    postReadyForDelivery(): Observable<unknown> {
        return this.http.post(`${environment.apiBaseUrl}/courier/ready-for-delivery`, {});
    }

    getActiveCouriers(): Observable<Courier[]> {
        return this.http.get<Courier[]>(`${environment.apiBaseUrl}/courier/get-couriers`)
            .pipe(
                map((couriers) => couriers.filter((courier) => courier.readyForDelivery))
            );
    }

    pushOrder(courierId: string, orderId: string): Observable<unknown> {
        return this.http.post(`${environment.apiBaseUrl}/courier/push-order`, { courierId, orderId });
    }

    uploadPhoto(orderId: string, blob: Blob, pictureType: PhotoType): Observable<unknown> {
        const formData = new FormData();
        formData.append('file', blob, `picture_${orderId}_${pictureType}`);
        return this.http.post(`${environment.apiBaseUrl}/courier/add-file/${orderId}/${pictureType}`, formData);
    }

    sendEmailDeliveredOrder(orderId: string, blob: Blob) {
        const formData = new FormData();
        formData.append('file', blob, `picture_${orderId}`);
        return this.http.post(`${environment.apiBaseUrl}/courier/send-photo-to-client/${orderId}`, formData);
    }

    setLocation(location: LocationPoint): Observable<unknown> {
        return this.http.post(`${environment.apiBaseUrl}/courier/set-position`, { points: [location] });
    }

    getPushedOrders(): Observable<Order[]> {
        return this.http.get<Order[]>(`${environment.apiBaseUrl}/courier/get-pushed-orders`)
            .pipe(map((order) => plainToClass(Order, order)));
    }

    declinePushedOrder(orderId: string): Observable<unknown> {
        return this.http.post(`${environment.apiBaseUrl}/courier/deny-pushed-order`, { orderId });
    }

    acceptPushedOrder(orderId: string): Observable<unknown> {
        return this.http.post(`${environment.apiBaseUrl}/courier/accept-pushed-order`, { orderId });
    }
}
